import { ApiErrorDTO, useVerificationCodeInviteCreate } from "@bvhi/backend-api/apis";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormCheckbox, LoadingButton } from "@bvhi/ui-base";
import { AddIcon, CancelIcon } from "@bvhi/ui-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, boolean, object, string } from "yup";
import { FormTextFieldChainNumber } from "../components/common/form-text-field-chain-number";
import { FormTextFieldEMail } from "../components/common/form-text-field-e-mail";
import { FormTextFieldOrganisationName } from "../components/common/form-text-field-organisation-name";
import { pick } from "../helpers/object";
import { useKeyboardShortcut } from "../hooks/use-keyboard-shortcuts";
import { useToast } from "../hooks/use-toast";
import { DialogError } from "./dialog-error";

interface Props {
  open: boolean;
  onClose: (success: boolean) => void;
}

interface FormValues {
  email: string;
  chainNumber: string;
  organisationName: string;
  resendEmail?: boolean;
}

export const AddAudiologistToSupplierDialog = ({ open, onClose }: Props) => {
  const { t } = useTranslation(["dialogs", "validation", "errors"]);
  const [showResendEmail, setShowResendEmail] = useState<boolean>(false);
  const [apiError, setApiError] = useState<ApiErrorDTO>();
  const { pushCustomErrorToast } = useToast();

  const inviteSchema: AnyObjectSchema = object({
    email: string().apacheMail(t("validation:email")).required(t("validation:required")),
    chainNumber: string().min(1, t("validation:string.min.1")).required(t("validation:required")),
    organisationName: string().min(1, t("validation:string.min.1")).required(t("validation:required")),
    resendEmail: boolean().optional(),
  });

  const { mutate: createVerificationCodeInvite, isPending: isCreateVerificationCodeInvitePending } = useVerificationCodeInviteCreate();

  const {
    formState: { isDirty, isValid, ...otherFormState },
    reset,
    handleSubmit,
    control,
    ...otherMethods
  } = useForm<FormValues>({
    resolver: yupResolver(inviteSchema),
    mode: "all",
    defaultValues: {
      email: "",
      chainNumber: "",
      organisationName: "",
      resendEmail: undefined,
    },
  });

  const handleClose = useCallback(
    (success: boolean) => {
      setShowResendEmail(false);
      reset({ email: "", chainNumber: "", resendEmail: undefined });
      setApiError(undefined);
      onClose(success);
    },
    [reset, onClose]
  );

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const requestBody = pick(data, ["email", "chainNumber", "organisationName"]);
    createVerificationCodeInvite(
      {
        data: { resendEmail: data.resendEmail ? data.resendEmail : false, ...requestBody },
      },
      {
        onSuccess: () => {
          setApiError(undefined);
          handleClose(true);
        },
        onError: (error) => {
          const shouldSHowResendEmailButton: string | undefined = [
            "ERR_VERIFICATION_CODE_CUSTOMER_RECENTLY_INVITED",
            "ERR_VERIFICATION_CODE_CHAIN_NUMBER_USED_IN_OTHER_INVITE",
          ].find((element) => {
            if (!error.errors) return undefined;
            return error.errors.find((error) => {
              if (error.code === element) return element;
            });
          });

          if (error.status && [400, 403, 409].includes(error.status)) {
            setApiError(error);
          }
          if (shouldSHowResendEmailButton) setShowResendEmail(true);
          if (error.status === 500) pushCustomErrorToast(t("dialogs:dialogs.add.audiologist.to.supplier.error.message"));
        },
      }
    );
  };

  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  useKeyboardShortcut({
    key: "Enter",
    onKeyPressed: () => primaryButtonRef.current?.click(),
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose(false);
      }}
    >
      <FormProvider
        control={control}
        formState={{ isDirty, isValid, ...otherFormState }}
        handleSubmit={handleSubmit}
        reset={reset}
        {...otherMethods}
      >
        <form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
          <DialogTitle>{t("dialogs:dialogs.add.audiologist.to.supplier.title")}</DialogTitle>
          <DialogContent>
            <DialogContentText pb={2}>{t("dialogs:dialogs.add.audiologist.to.supplier.description")}</DialogContentText>
            <FormTextFieldEMail />

            <FormTextFieldChainNumber />

            <FormTextFieldOrganisationName />
            {showResendEmail && (
              <FormCheckbox
                label={t("dialogs:dialogs.add.audiologist.to.supplier.form.text.field.resent.email.label")}
                name="resendEmail"
              />
            )}
          </DialogContent>
          <DialogError
            color="error"
            customError={showResendEmail ? t("errors:custom.error.code.RESENT_MAIL") : undefined}
            error={apiError}
          />
          <DialogActions sx={{ pt: 2 }}>
            <Button
              startIcon={<CancelIcon />}
              variant="text"
              onClick={() => {
                handleClose(false);
              }}
            >
              {t("dialogs:dialogs.cancel")}
            </Button>
            <LoadingButton
              ref={primaryButtonRef}
              disabled={!isDirty}
              loading={isCreateVerificationCodeInvitePending}
              startIcon={<AddIcon />}
              type="submit"
              variant="contained"
            >
              {t("dialogs:dialogs.add.audiologist.to.supplier.submit")}
            </LoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
